import { useState, type ReactElement, type ReactNode } from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, IconButton, Typography, useTheme } from '@mui/material'

export type CorneredSectionProps = {
  /** Without a title the card renders its content only, with no header row. */
  title?: string
  collapsible?: boolean
  defaultOpen?: boolean
  summary?: ReactNode
  actions?: ReactNode
  children?: ReactNode
}

/**
 * Section variant for drawers with a light grey background: the whole section — title row and
 * content — is a white card with rounded corners. Carries no outer margin of its own — the drawer
 * container owns the gutters and the spacing between stacked sections.
 */
export const CorneredSection = ({
  title,
  collapsible,
  defaultOpen = true,
  summary,
  actions,
  children,
}: CorneredSectionProps): ReactElement => {
  const theme = useTheme()
  const [open, setOpen] = useState(defaultOpen)
  const hasHeader = Boolean(title) || Boolean(actions) || Boolean(collapsible)
  return (
    <Box
      sx={{
        flexShrink: 0,
        backgroundColor: theme.palette.common.white,
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
        p: 1.5,
      }}
    >
      <Box
        sx={{
          display: hasHeader ? 'flex' : 'none',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.primary,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 0,
          }}
        >
          {summary && collapsible && !open ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {summary}
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {actions}
            {collapsible ? (
              <IconButton size="small" onClick={() => setOpen(o => !o)}>
                {open ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
              </IconButton>
            ) : null}
          </Box>
        </Box>
      </Box>
      {!collapsible || open ? <Box sx={{ pt: hasHeader ? 1 : 0 }}>{children}</Box> : null}
    </Box>
  )
}
